import React, { useMemo, useState } from 'react';
import { Box, Container, Grid } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslate } from 'react-admin';

import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';

import { DashboardV2Header } from './components/DashboardV2Header';
import { KpiCard } from './components/KpiCard';
import { PlatformDonutCard } from './components/PlatformDonutCard';
import { PerformanceCard } from './components/PerformanceCard';
import { CoinDistributionCard } from './components/CoinDistributionCard';
import { UserVolumeDistributionCard } from './components/UserVolumeDistributionCard';
import { useDashboardV2Data } from './hooks/useDashboardV2Data';
import type { DashboardV2TabKey, QuickRange } from './hooks/useDashboardV2Data';

export default function MainPage() {
  const t = useTranslate();

  const [tab, setTab] = useState<DashboardV2TabKey>('perpetuals');
  const [range, setRange] = useState<QuickRange>('30d');

  // 默认 30d（本机时区）
  const now = dayjs();
  const [from, setFrom] = useState<Dayjs>(() => now.subtract(30, 'day'));
  const [to, setTo] = useState<Dayjs>(() => now);

  const onSetRange = (r: QuickRange) => {
    setRange(r);

    const n = dayjs();

    if (r === '24h') {
      setFrom(n.subtract(24, 'hour'));
      setTo(n);
    } else if (r === '7d') {
      setFrom(n.subtract(7, 'day'));
      setTo(n);
    } else if (r === '30d') {
      setFrom(n.subtract(30, 'day'));
      setTo(n);
    } else if (r === 'all') {
      // mock 用 365 天
      setFrom(n.subtract(365, 'day'));
      setTo(n);
    } else {
      // custom：不动 from/to（由 Timeframe 选择来设置）
    }
  };

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  );

  const { data, loading } = useDashboardV2Data({ tab, range, from, to, timezone });


  const kpiTop = useMemo(() => data?.kpisTop ?? [], [data]);
  const kpiMid = useMemo(() => data?.kpisMid ?? [], [data]);

  return (

    <PermissionGuard resource="perpetuals" action="read" fallback={<ErrorPage403 />}>
      <Container maxWidth={false} sx={{ py: 4, bgcolor: 'transparent' }}>
        <DashboardV2Header
          title={t('module.dashboardV2.title', { _: 'Dashboard V2' })}
          tab={tab}
          onTabChange={setTab}
          from={from}
          to={to}
          onFromChange={(v) => setFrom(v)}
          onToChange={(v) => setTo(v)}
          range={range}
          onRangeChange={onSetRange}
          exporting={data?.exporting ?? false}
          onExport={data?.exportCSV}
          onApplyTimeframe={() => {
            setRange('custom');
          }}
        />

        {/* KPI Row */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {kpiTop.map((k) => (
            <Grid key={k.label} item xs={12} sm={6} md={3}>
              <KpiCard kpi={k} loading={loading} />
            </Grid>
          ))}
        </Grid>

        {/* Mid KPI Row */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {kpiMid.map((k) => (
            <Grid key={k.label} item xs={12} md={6}>
              <KpiCard kpi={k} loading={loading} />
            </Grid>
          ))}
        </Grid>

        {/* Charts Row 1 */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={5}>
            <PlatformDonutCard
              title={t('module.dashboardV2.platformDistribution', { _: 'Platform Distribution' })}
              items={data?.platforms ?? []}
              totals={data?.platformTotals}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <PerformanceCard
              title={t('module.dashboardV2.performance.title', { _: 'Performance' })}
              data={data?.performance ?? []}
              loading={loading}
            />
          </Grid>
        </Grid>

        {/* Charts Row 2 */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <CoinDistributionCard
              title={t('module.dashboardV2.coinDistribution', { _: 'Coin Distribution' })}
              data={data?.coinDist ?? []}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <UserVolumeDistributionCard
              title={t('module.dashboardV2.userVolumeDistribution', { _: 'User Volume Distribution' })}
              data={data?.userVolumeDist ?? []}
              loading={loading}
              totalVolume={data?.totalVolume || 0}
            />
          </Grid>
        </Grid>

        <Box sx={{ height: 24 }} />
      </Container>
    </PermissionGuard>
  );
}
